import { Link } from "react-router-dom";
import { SectionEyebrow, SectionTitle, FadeUp } from "./_shared";
import CpiRadar from "./CpiRadar";
import { SAMPLE_PROFILE, TIERS, CPI_DOMAINS } from "@/data/cpi";
import { TierPriorityMap } from "./_partials/TierPriorityMap";

const PROFILE_IMAGE =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/d5r7kph3_abstract_human_figure_with_six_glowing_modality_nodes_connec.png";

const byCode = Object.fromEntries(CPI_DOMAINS.map((domain) => [domain.code, domain]));

const TopSystems = () => (
  <div data-testid="sample-top-systems" className="h-full p-6 md:p-7 flex flex-col justify-center">
    <div className="font-mono text-xs uppercase tracking-[0.16em] text-primary/55 mb-5">Top Systems</div>
    <ul className="space-y-5">
      {SAMPLE_PROFILE.topSystems.map((code) => (
        <li key={code}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-display text-sm font-semibold text-foreground/85">{byCode[code].name}</span>
            <span className="font-mono text-base font-bold text-accent">{byCode[code].score}</span>
          </div>
          <div className="mt-2 h-2 bg-primary/10 overflow-hidden">
            <div
              data-testid={`sample-system-bar-${code.toLowerCase()}`}
              className="h-full bg-primary"
              style={{ width: `${byCode[code].score}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  </div>
);

export default function SampleAnalysisSection() {
  return (
    <section id="sample-analysis" data-testid="sample-analysis-section" className="py-24 md:py-32 bg-background">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <FadeUp className="text-center max-w-3xl mx-auto">
          <SectionEyebrow>Sample Analysis</SectionEyebrow>
          <SectionTitle className="mt-4 text-[clamp(1.9rem,4.5vw,3.25rem)]">
            From Data to an <em className="not-italic text-accent">Optimization Plan</em>
          </SectionTitle>
          <p className="mt-5 text-lg text-foreground/70">A fictional CPI profile, visualized.</p>
        </FadeUp>

        <FadeUp className="mt-14">
          <div data-testid="sample-archetype-card" className="border-2 border-primary/15 bg-background overflow-hidden">
            <div className="border-b border-border p-5 md:px-7 md:py-6">
              <div className="font-mono text-xs uppercase tracking-[0.16em] text-primary/55">
                Primary Archetype · {SAMPLE_PROFILE.id}
              </div>
              <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-primary leading-tight">
                {SAMPLE_PROFILE.archetype.name}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-4 relative min-h-[360px] md:min-h-[430px] overflow-hidden border-b md:border-b-0 md:border-r border-border">
                <img
                  src={PROFILE_IMAGE}
                  alt="Connected physiological systems representing an integrated CPI profile"
                  data-testid="sample-profile-image"
                  className="absolute inset-0 h-full w-full object-cover object-center"
                  loading="lazy"
                />
              </div>
              <div className="md:col-span-4 border-b md:border-b-0 md:border-r border-border p-5 flex items-center justify-center bg-card">
                <CpiRadar
                  domains={CPI_DOMAINS}
                  testId="sample-profile-cpi-radar"
                  ariaLabel="Sample CPI profile across ten connected domains"
                  labelSize={13}
                  className="w-full max-w-[330px]"
                />
              </div>
              <div className="md:col-span-4">
                <TopSystems />
              </div>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-5">
          <TierPriorityMap tiers={TIERS} />
        </FadeUp>

        <FadeUp delay={0.15} className="mt-9 text-center">
          <Link to="/sample-analysis" data-testid="see-sample-analysis-cta" className="iw-btn-ghost inline-flex items-center gap-2 px-8 py-4 text-xs">
            See Sample Analysis <span aria-hidden>→</span>
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}